import { Ban, CheckCircle2, ClipboardCheck, ClipboardList, Clock, Eye, FileText, GraduationCap, XCircle } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ErrorState } from '../../../../components/ErrorState';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { ProfileBanner, type ProfileBannerTone } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText, type StatusTone } from '../../../../components/table/StatusText';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly, formatDateTime, toIso } from '../../../../lib/formatDate';
import { SurveyAnswerModal } from '../../../learning-development/survey/components/SurveyAnswerModal';
import { describeTargets } from '../../../learning-development/survey/utils/describeTargets';
import { MY_SURVEY_ANSWER_SOURCE, mySurveysApi } from '../mySurveysApi';
import { MY_SURVEY_STAGE, MY_SURVEY_STAGE_LABELS, type MySurvey, type MySurveyStageValue } from '../mySurveyTypes';
import { MySurveyDevelopmentPlanModal } from '../components/MySurveyDevelopmentPlanModal';
import { MySurveyPreviewPanel } from '../components/MySurveyPreviewPanel';
import { MySurveyTrainingReportCard } from '../components/MySurveyTrainingReportCard';

/**
 * One survey, as the employee it covers sees it.
 *
 * On the **header** layout with two tabs: **Info** — the survey, its questionnaire and the trail —
 * and **Preview**, two sub-tabs (Survey: the reader's own answers · Training Recommendations: the
 * trainings sent to them, each opening its own profile, where its report lives). There is NO Life
 * cycle tab (product call, 13 Oct 2026 — removed): where the response stands is the header's pill,
 * the latest STEP (Deleted wins), and the banner's sentence.
 * The one thing to DO is the banner's: Answer, View my answers once they have, and — once the plan
 * is approved — View development plan, a popup to read it and accept or decline it.
 *
 * Every card is somebody else's record or a state moved by an action, so none carries Edit: the
 * survey is Learning & Development's, and its Details cog links there for a reader who also
 * administers it.
 */
type TabKey = 'info' | 'preview';

export function MySurveyProfilePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [survey, setSurvey] = useState<MySurvey | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [answering, setAnswering] = useState(false);
  const [tab, setTab] = useState<TabKey>('info');
  // The approved plan's popup — "View development plan".
  const [viewingPlan, setViewingPlan] = useState(false);

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      mySurveysApi
        .get(id, signal)
        .then((next) => {
          if (!signal?.aborted) {
            setSurvey(next);
            setError(null);
          }
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(caught instanceof ApiError ? caught : new ApiError('Could not load this survey.', 0));
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  const breadcrumb: BreadcrumbSegment[] = [
    'Self Service',
    'Learning & Development',
    { label: 'Survey', to: '/self-service/surveys' },
    survey ? survey.title : 'Survey',
  ];

  // A failed REFRESH keeps the page; only a first load that failed replaces it.
  if (error && !survey) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} correlationId={error.correlationId} onRetry={() => load()} />
      </div>
    );
  }

  if (!survey) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  const questionnaire = survey.questionnaire ?? null;
  const answered = survey.respondedAt != null;
  const answerable = survey.isActive && questionnaire?.isActive === true;
  const state = bannerFor(survey, answerable);
  // Whether the plan OPENS: approved (or answered since) AND sent — approved alone is not the reader's yet.
  const planOpens = survey.isActive && PLAN_STAGES.includes(survey.stage) && survey.developmentPlanSentAt != null;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="my-survey-profile"
        header={
          <ProfileHeader
            id="my-survey-profile-header"
            icon={<ClipboardList className="h-6 w-6" aria-hidden="true" />}
            title={survey.title}
            status={
              // Before assessment there is no stage to show — the survey's own Active stands in.
              !survey.isActive ? (
                <StatusText tone="inactive">Deleted</StatusText>
              ) : survey.stage >= MY_SURVEY_STAGE.Assessed ? (
                <StatusText tone={STAGE_TONES[survey.stage]}>{MY_SURVEY_STAGE_LABELS[survey.stage]}</StatusText>
              ) : (
                <StatusText tone="active">Active</StatusText>
              )
            }
            createdBy={survey.createdByName}
            createdAt={survey.createdAt}
          />
        }
        banner={
          <ProfileBanner
            id="my-survey-profile-banner"
            tone={state.tone}
            icon={state.icon}
            message={state.message}
            // The ACTION is gated; the sentence is for every reader. It only opens the form, whose
            // own tick is the confirmation.
            action={
              /*
                ONCE APPROVED the plan is the employee's to read — and to accept or decline — and THAT
                is what the banner offers (product call, 12 Oct 2026): a POPUP with the plan, which is
                where it is accepted or declined; Preview reads it too. Accepted and Declined keep the button, so the
                plan and the answer given can still be read.
              */
              /*
                THE PLAN IS READ HERE; its process moved to the TRAINING RECOMMENDATIONS (product
                call, 13 Oct 2026). The employee now answers, trains and reports per recommendation
                sent to them — Preview › Training Recommendations — so the banner no longer offers
                Start / Complete training or Submit report on the plan. A training or report recorded
                on the plan BEFORE that stays readable below and under Preview › Report.
              */
              answered && planOpens ? (
                <div className="flex items-center gap-2">
                  <Button
                    id="my-survey-view-plan"
                    variant="secondary"
                    leading={<FileText className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => setViewingPlan(true)}
                  >
                    View development plan
                  </Button>
                  <Button
                    id="my-survey-view-recommendations"
                    variant="primary"
                    leading={<GraduationCap className="h-4 w-4" aria-hidden="true" />}
                    // It only NAVIGATES to the tab where each recommendation's own forms are.
                    onClick={() => setTab('preview')}
                  >
                    Training recommendations
                  </Button>
                </div>
              ) : answered ? (
                <Button
                  id="my-survey-view"
                  variant="secondary"
                  leading={<Eye className="h-4 w-4" aria-hidden="true" />}
                  onClick={() => setAnswering(true)}
                >
                  View my answers
                </Button>
              ) : answerable ? (
                <PermissionGate permission="my-surveys.respond">
                  <Button
                    id="my-survey-answer"
                    variant="primary"
                    leading={<ClipboardCheck className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => setAnswering(true)}
                  >
                    Answer
                  </Button>
                </PermissionGate>
              ) : undefined
            }
          />
        }
        tabs={[
          { key: 'info', label: 'Info' },
          // The reader's OWN answers, in words — product call, 12 Oct 2026. The page's own read.
          { key: 'preview', label: 'Preview' },
        ]}
        activeTab={tab}
        onTabChange={(next) => setTab(next as TabKey)}
        // Preview owns its own chrome — a second strip of folder tabs — so it runs edge to edge.
        panel={tab === 'preview' ? 'bleed' : 'cards'}
      >
        {tab === 'preview' ? (
          // Keyed on the answer's instant, so answering from the banner re-reads it.
          <MySurveyPreviewPanel
            key={survey.respondedAt ?? 'unanswered'}
            surveyId={survey.id}
            answered={survey.respondedAt != null}
            // A deleted survey's recommendation tracks stay readable, with nothing offered on them.
            readOnly={!survey.isActive}
          />
        ) : (
          <>
            <ProfileCard
              id="my-survey-profile-details"
              title="Details"
              // Learning & Development's record: no Edit here, and the way to it for a reader who has the key.
              menuItems={[
                {
                  key: 'open',
                  label: 'Open in Learning & Development',
                  permission: 'surveys.read',
                  onClick: () => navigate(`/learning-development/survey/${survey.id}`),
                },
              ]}
            >
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Title', value: survey.title },
                  { label: 'Units/Organization', value: describeTargets(survey.orgUnits, 'All units/organizations') },
                  { label: 'Position', value: describeTargets(survey.positions, 'All positions') },
                ]}
              />
            </ProfileCard>

            {/* Moved by the banner's action, never by typing — so no Edit. */}
            <ProfileCard id="my-survey-profile-questionnaire" title="Questionnaire">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Title', value: questionnaire?.title ?? null },
                  { label: 'Description', value: questionnaire?.description ?? null },
                  {
                    label: 'Responded',
                    value: answered ? (
                      <time dateTime={toIso(survey.respondedAt!)} className="font-mono">
                        {formatDateTime(survey.respondedAt!)}
                      </time>
                    ) : null,
                  },
                ]}
              />
            </ProfileCard>

            {/*
              THE TRAINING, once the plan is ACCEPTED — a conditional SECTION, never a panel. One per
              plan; its Edit is the banner's own form, carrying the key of what it writes.
            */}
            {survey.stage === MY_SURVEY_STAGE.Accepted && survey.training != null ? (
              <ProfileCard
                id="my-survey-profile-training"
                title="Training"
                // No Edit: the plan's own training is no longer recorded here — training is taken per
                // recommendation now. This card is the record of one recorded before that.
              >
                <PreviewTable
                  variant="table-display"
                  rows={[
                    { label: 'Title', value: survey.training?.title ?? null },
                    // Calendar dates — `formatDateOnly`, never the instant path.
                    { label: 'Start date', value: survey.training ? formatDateOnly(survey.training.startDate) : null, mono: true },
                    { label: 'End date', value: survey.training ? formatDateOnly(survey.training.endDate) : null, mono: true },
                    // Why it was last corrected — an em dash while it never has been.
                    { label: 'Remarks', value: survey.training?.remarks ?? null },
                    {
                      label: 'Completed',
                      value:
                        survey.training?.completedAt != null ? (
                          <time dateTime={toIso(survey.training.completedAt)} className="font-mono">
                            {formatDateTime(survey.training.completedAt)}
                          </time>
                        ) : null,
                    },
                    { label: 'Completion remarks', value: survey.training?.completionRemarks ?? null },
                  ]}
                />
              </ProfileCard>
            ) : null}

            {/*
              The report on that EARLIER plan-level training, where one was submitted — read only,
              so no Edit. Its home since Preview's Report sub-tab was removed (13 Oct 2026); a
              recommendation's report lives on that recommendation's own profile.
            */}
            {survey.training != null && survey.training.completedAt != null && survey.training.reportAt != null ? (
              <MySurveyTrainingReportCard
                id="my-survey-profile-training-report"
                training={survey.training}
                fetchAttachment={(attachmentId) => mySurveysApi.downloadTrainingReportAttachment(survey.id, attachmentId)}
              />
            ) : null}

            <ProfileCard id="my-survey-profile-audit" title="Audit">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Created by', value: survey.createdByName },
                  {
                    label: 'Created',
                    value: (
                      <time dateTime={toIso(survey.createdAt)} className="font-mono">
                        {formatDateTime(survey.createdAt)}
                      </time>
                    ),
                  },
                  { label: 'Updated by', value: survey.updatedByName },
                  {
                    label: 'Updated',
                    value: survey.updatedAt ? (
                      <time dateTime={toIso(survey.updatedAt)} className="font-mono">
                        {formatDateTime(survey.updatedAt)}
                      </time>
                    ) : null,
                  },
                ]}
              />
            </ProfileCard>
          </>
        )}
      </ProfileHeaderShell>

      {viewingPlan ? (
        <MySurveyDevelopmentPlanModal
          surveyId={survey.id}
          surveyActive={survey.isActive}
          // Read only: the plan is no longer accepted or declined — each recommendation is.
          decidable={false}
          onClose={() => setViewingPlan(false)}
          onDecided={() => {
            setViewingPlan(false);
            load();
          }}
        />
      ) : null}

      {answering && questionnaire ? (
        <SurveyAnswerModal
          open
          surveyId={survey.id}
          surveyActive={survey.isActive}
          questionnaire={questionnaire}
          source={MY_SURVEY_ANSWER_SOURCE}
          onClose={() => setAnswering(false)}
          onAnswered={() => {
            setAnswering(false);
            load();
          }}
        />
      ) : null}
    </div>
  );
}

/** The header pill's tone per step — the profile may carry amber; an index page may not. */
const STAGE_TONES: Record<MySurveyStageValue, StatusTone> = {
  0: 'pending',
  1: 'inactive',
  2: 'inactive',
  3: 'inactive',
  4: 'pending',
  5: 'active',
  6: 'blocked',
  7: 'active',
  8: 'blocked',
};

/** The steps at which the plan was approved — and so opens from the banner ONCE IT HAS BEEN SENT. */
const PLAN_STAGES: readonly MySurveyStageValue[] = [MY_SURVEY_STAGE.Approved, MY_SURVEY_STAGE.Accepted, MY_SURVEY_STAGE.Declined];

/**
 * The banner's sentence for every step — it renders for each state, not only the bad ones. Dates
 * are the step's own; nothing here quotes the plan or a rejection's reason.
 */
function bannerFor(survey: MySurvey, answerable: boolean): { tone: ProfileBannerTone; icon: React.ReactNode; message: string } {
  const icon = (Glyph: typeof Clock) => <Glyph className="h-4 w-4" aria-hidden="true" />;
  const on = (at: string | null | undefined) => (at != null ? ` on ${formatDateTime(at)}` : '');

  if (!survey.isActive) {
    return survey.respondedAt != null
      ? { tone: 'red', icon: icon(Ban), message: 'This survey is deleted. Nothing is erased — your answers are kept.' }
      : { tone: 'red', icon: icon(Ban), message: 'This survey is deleted, so it can no longer be answered.' };
  }

  switch (survey.stage) {
    case MY_SURVEY_STAGE.NotAnswered:
      return answerable
        ? { tone: 'amber', icon: icon(ClipboardCheck), message: 'Learning & Development asks for your answers to this survey.' }
        : { tone: 'red', icon: icon(Ban), message: 'This questionnaire is deleted, so it can no longer be answered.' };
    case MY_SURVEY_STAGE.Answered:
      return { tone: 'slate', icon: icon(CheckCircle2), message: `You answered this survey${on(survey.respondedAt)}. Learning & Development has not assessed it yet.` };
    case MY_SURVEY_STAGE.Assessed:
      return { tone: 'slate', icon: icon(CheckCircle2), message: `Your response was assessed${on(survey.assessedAt)}. A development plan is the next step.` };
    case MY_SURVEY_STAGE.DevelopmentPlan:
      return { tone: 'slate', icon: icon(FileText), message: `Your development plan was written${on(survey.developmentPlanAt)}. It has not been submitted for approval yet.` };
    case MY_SURVEY_STAGE.Submitted:
      return { tone: 'amber', icon: icon(Clock), message: `Your development plan is waiting for approval — submitted${on(survey.approval?.submittedAt)}.` };
    case MY_SURVEY_STAGE.Approved:
      return {
        tone: 'emerald',
        icon: icon(CheckCircle2),
        // APPROVED IS NOT SENT: until Learning & Development sends it, there is nothing here to read.
        message:
          survey.developmentPlanSentAt != null
            ? `Your development plan was approved${on(survey.approval?.decidedAt)}. Read it, then answer the trainings recommended for you under Training recommendations.`
            : `Your development plan was approved${on(survey.approval?.decidedAt)}. Learning & Development has not sent it to you yet.`,
      };
    case MY_SURVEY_STAGE.Accepted:
      return {
        tone: 'emerald',
        icon: icon(CheckCircle2),
        message:
          survey.training != null
            ? survey.training.completedAt != null
              ? survey.training.reportAt != null
                ? `You completed your training, ${survey.training.title}, and submitted your report${on(survey.training.reportAt)}.`
                : `You completed your training, ${survey.training.title}${on(survey.training.completedAt)}.`
              : survey.training.canComplete === true
                ? `Your training, ${survey.training.title}, ended on ${formatDateOnly(survey.training.endDate)}.`
                : `Your training: ${survey.training.title}, ${formatDateOnly(survey.training.startDate)} to ${formatDateOnly(survey.training.endDate)}.`
            : `You accepted your development plan${on(survey.planDecidedAt)}. The trainings recommended for you are under Training recommendations.`,
      };
    case MY_SURVEY_STAGE.Declined:
      return {
        tone: 'red',
        icon: icon(XCircle),
        message: `You declined your development plan${on(survey.planDecidedAt)}. Learning & Development will revise it and send it for approval again.`,
      };
    case MY_SURVEY_STAGE.Rejected:
      return { tone: 'red', icon: icon(XCircle), message: `Your development plan was not approved${on(survey.approval?.decidedAt)}. Learning & Development can revise it and submit it again.` };
  }
}
